import React from 'react';
import { Button, Avatar, Card, Modal } from 'antd';
import { func, shape } from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faClose,
  faComments,
  faStar,
  faTimesCircle,
  faUser,
  faUserXmark,
} from '@fortawesome/pro-light-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';

import ColouredPill from 'Src/common/components/colouredPill';
import { MENTORSHIP_STATUS } from 'Src/mentorship/constants';
import I18nCustomFormatter from 'Src/common/components/i18nCustomFormatter';

const { confirm } = Modal;

function ConnectionCard({
  data,
  acceptRequest,
  declineRequest,
  closeConnection,
  withdrawRequest,
  openMessages,
  openRateConversationModal,
}) {
  const { mentee, mentor, status } = data;
  const isMentor = window.userData.id === mentor.id;

  const handleConfirm = (title, content, Icon, handler) => () => {
    confirm({
      title,
      content,
      className: 'connection-confirm',
      icon: <FontAwesomeIcon icon={Icon} />,
      cancelButtonProps: { className: 'cancel-btn' },
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        handler(data);
      },
    });
  };

  const getButton = (text, handler, Icon) => (
    <Button
      onClick={() => handler(data)}
      icon={<FontAwesomeIcon className="mr8 arc-color-primary" icon={Icon} />}
      className="connections-action-button arc-d-flex arc-flex-vertical-align">
      {text}
    </Button>
  );

  const requestReceivedButtons = () => (
    <>
      {getButton('Accept', acceptRequest, faCheckCircle)}
      {getButton(
        'Decline',
        handleConfirm('Decline request', 'Are you sure you want to decline this request?', faUserXmark, declineRequest),
        faTimesCircle,
      )}
    </>
  );

  const requestSentButton = () =>
    getButton(
      'Withdraw',
      handleConfirm(
        'Withdraw request',
        'Are you sure you want to withdraw this request?',
        faUserXmark,
        withdrawRequest,
      ),
      faTimesCircle,
    );

  const defaultButtons = () => (
    <>
      {getButton('Messages', openMessages, faComments)}
      {getButton(
        'Close connection',
        handleConfirm(
          'Close connection',
          'Are you sure you want to close this connection?',
          faUserXmark,
          closeConnection,
        ),
        faClose,
      )}
    </>
  );

  const rateConversationButton = () => {
    const ratingText = (rating) => (
      <p>
        <I18nCustomFormatter id="rating-text" values={{ rating }} />
        <FontAwesomeIcon icon={faStarSolid} />
      </p>
    );

    if (isMentor && mentor.rating) {
      return ratingText(mentor.rating);
    }
    if (!isMentor && mentee.rating) {
      return ratingText(mentee.rating);
    }

    return getButton('Rate your conversation', openRateConversationModal, faStar);
  };

  const getFormattedData = (pillText, key, redirectUrl, buttons) => ({
    pillText,
    key,
    redirectUrl,
    buttons,
  });

  const getData = () => {
    const mentorUrl = `/mentorship/mentors?mentor_profile_id=${mentor.id}`;
    const menteeUrl = mentee.profile_url;

    if (status === 5) {
      return getFormattedData('Completed', 'completed', isMentor ? menteeUrl : mentorUrl, rateConversationButton);
    }

    if (isMentor) {
      if (status === 1) {
        return getFormattedData('New request', 'sent', menteeUrl, requestReceivedButtons);
      }
      if (status === 2) {
        return getFormattedData('Mentee', 'connected', menteeUrl, defaultButtons);
      }
    } else {
      if (status === 1) {
        return getFormattedData('Request sent', 'sent', mentorUrl, requestSentButton);
      }
      if (status === 2) {
        return getFormattedData('Mentor', 'connected', mentorUrl, defaultButtons);
      }
    }

    return getFormattedData('Withdrawn', 'sent', menteeUrl, () => {});
  };

  const { pillText, key, redirectUrl, buttons } = getData();

  return (
    <Card>
      <div className="arc-d-flex connection-card-wrapper">
        <Avatar
          shape="square"
          size={40}
          src={isMentor ? mentee?.present_picture : mentor?.present_picture}
          icon={<FontAwesomeIcon icon={faUser} />}
        />
        <div className="ml8">
          <div className="mb2">
            <ColouredPill data={{ label: pillText, ...MENTORSHIP_STATUS[key] }} />
          </div>
          <a className="arc-p arc-font-weight-bold-imp" href={redirectUrl}>
            {isMentor ? mentee?.full_name : mentor?.full_name}
          </a>
          <p className="arc-p arc-color-gray10">
            <I18nCustomFormatter id="seeking-help-label" values={{ service: data?.service?.name }} />
          </p>
          <div className="arc-d-flex mt8 action-buttons-wrapper">{buttons()}</div>
        </div>
      </div>
    </Card>
  );
}

ConnectionCard.defaultProps = {
  closeConnection: () => {},
};

ConnectionCard.propTypes = {
  acceptRequest: func.isRequired,
  closeConnection: func,
  data: shape().isRequired,
  declineRequest: func.isRequired,
  openMessages: func.isRequired,
  openRateConversationModal: func.isRequired,
  withdrawRequest: func.isRequired,
};

export default ConnectionCard;
